import React from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";

import {
  Building2,
  Landmark,
  RefreshCw,
  Toolbox,
  ChevronRight,
} from "lucide-react";

import { useToast } from "../../../components/feedback/ToastProvider";

import "./AdminOrganisationsPage.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

function useAnimatedNumber(targetValue, duration = 700) {
  const [value, setValue] = React.useState(0);

  React.useEffect(() => {
    const target = Number(targetValue) || 0;

    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setValue(target);

      return undefined;
    }

    let frame;

    let startedAt = null;

    function animate(timestamp) {
      if (!startedAt) startedAt = timestamp;

      const progress = Math.min((timestamp - startedAt) / duration, 1);

      const eased = 1 - Math.pow(1 - progress, 3);

      setValue(target * eased);

      if (progress < 1) {
        frame = requestAnimationFrame(animate);
      } else {
        setValue(target);
      }
    }

    frame = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(frame);
  }, [targetValue, duration]);

  return value;
}

function AnimatedNumber({ value, locale = "en-GB" }) {
  const animated = useAnimatedNumber(value);

  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: 0,
  }).format(Math.round(animated));
}

function AnimatedCurrency({ value, locale = "en-GB" }) {
  const animated = useAnimatedNumber(value, 850);

  return new Intl.NumberFormat(locale, {
    style: "currency",

    currency: "EUR",

    notation: "compact",

    maximumFractionDigits: 1,
  }).format(animated);
}

function formatRegion(countryCode, region, t) {
  const country = countryCode ? t(`countries.${countryCode}`, countryCode) : "";

  const regionName = region ? t(`regions.${region}`, region) : "";

  return [country, regionName].filter(Boolean).join(" / ");
}

function getInitials(name) {
  if (!name) return "—";

  return name

    .trim()

    .split(/\s+/)

    .slice(0, 2)

    .map((part) => part.charAt(0))

    .join("")

    .toUpperCase();
}

function SummaryItem({ label, value, locale, delay = 0 }) {
  return (
    <div
      className="admin-organisations-summary__item"
      style={{ "--summary-delay": `${delay}ms` }}
    >
      <span>{label}</span>

      <strong>
        <AnimatedNumber value={value} locale={locale} />
      </strong>
    </div>
  );
}

function OrganisationRow({
  organisation,

  type,

  locale,

  t,

  usersLabel,

  recordsLabel,

  projectsLabel,

  amountLabel,

  openLabel,

  delay = 0,

  onOpen,

  onTooltipMove,

  onTooltipHide,
}) {
  const isLender = type === "LENDER";

  const secondaryCount = isLender
    ? Number(
        organisation.record_count ??
          organisation.loan_records ??
          organisation.loan_case_count ??
          0,
      )
    : Number(
        organisation.project_count ??
          organisation.projects ??
          organisation.renovation_projects ??
          0,
      );

  const amount = isLender
    ? Number(
        organisation.registered_loan_amount ??
          organisation.total_loan_amount ??
          0,
      )
    : Number(
        organisation.renovation_value ??
          organisation.total_renovation_value ??
          0,
      );

  const region = formatRegion(
    organisation.country_code,

    organisation.region,

    t,
  );

  const initials = organisation.initials || getInitials(organisation.name);

  return (
    <article
      className="admin-organisations-row"
      style={{
        "--row-delay": `${delay}ms`,
      }}
      role="button"
      tabIndex={0}
      aria-label={`${openLabel}: ${organisation.name}`}
      onClick={() => onOpen(organisation.id)}
      onMouseMove={(event) => {
        onTooltipMove({
          organisationId: organisation.id,

          x: event.clientX,

          y: event.clientY,
        });
      }}
      onMouseLeave={onTooltipHide}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();

          onOpen(organisation.id);
        }
      }}
    >
      <div
        className={`admin-organisations-row__avatar ${
          organisation.logo_url ? "admin-organisations-row__avatar--image" : ""
        }`}
      >
        {organisation.logo_url ? (
          <img src={organisation.logo_url} alt="" />
        ) : (
          <span>{initials}</span>
        )}
      </div>

      <div className="admin-organisations-row__content">
        <strong className="admin-organisations-row__name">
          {organisation.name}
        </strong>

        <div className="admin-organisations-row__meta">
          {region ? <span>{region}</span> : null}

          {region ? (
            <span
              className="admin-organisations-row__separator"
              aria-hidden="true"
            >
              •
            </span>
          ) : null}

          <span>
            {Number(organisation.user_count || 0)} {usersLabel}
          </span>

          <span
            className="admin-organisations-row__separator"
            aria-hidden="true"
          >
            •
          </span>

          <span>
            {secondaryCount} {isLender ? recordsLabel : projectsLabel}
          </span>
        </div>
      </div>

      <div className="admin-organisations-row__amount">
        <strong>
          <AnimatedCurrency value={amount} locale={locale} />
        </strong>

        <span>{amountLabel}</span>
      </div>

      <ChevronRight
        size={18}
        strokeWidth={2}
        className="admin-organisations-row__chevron"
        aria-hidden="true"
      />
    </article>
  );
}

function OrganisationSection({
  title,

  subtitle,

  organisations,

  type,

  locale,

  t,

  onOpen,

  onTooltipMove,

  onTooltipHide,
}) {
  const isLender = type === "LENDER";

  return (
    <section className="admin-organisations-panel">
      <header className="admin-organisations-panel__header">
        <div className="admin-organisations-panel__heading">
          <div className="admin-organisations-panel__icon">
            {isLender ? <Landmark size={18} /> : <Toolbox size={18} />}
          </div>

          <h2>{title}</h2>
        </div>

        <div className="admin-organisations-panel__header-meta">
          <span>{subtitle}</span>

          <strong>
            {t("common.organisationCount", {
              count: organisations.length,

              defaultValue: "{{count}} organisations",
            })}
          </strong>
        </div>
      </header>

      <div className="admin-organisations-panel__body">
        {organisations.length > 0 ? (
          <div className="admin-organisations-list">
            {organisations.map((organisation, index) => (
              <OrganisationRow
                key={organisation.id ?? organisation.name}
                organisation={organisation}
                type={type}
                locale={locale}
                t={t}
                delay={index * 55}
                usersLabel={t("common.users", "users")}
                recordsLabel={t("lenders.records", "lender-created records")}
                projectsLabel={t("oss.projects", "renovation projects")}
                amountLabel={
                  isLender
                    ? t("lenders.amountLabel", "registered loan amount")
                    : t("oss.amountLabel", "renovation project value")
                }
                openLabel={t("actions.openOrganisation", "Open organisation")}
                onOpen={onOpen}
                onTooltipMove={onTooltipMove}
                onTooltipHide={onTooltipHide}
              />
            ))}
          </div>
        ) : (
          <div className="admin-organisations-empty">
            <Building2 size={23} />

            <strong>
              {isLender
                ? t("lenders.emptyTitle", "No lender organisations")
                : t("oss.emptyTitle", "No OSS organisations")}
            </strong>

            <span>
              {t(
                "common.emptyDescription",

                "No organisations are available for this category yet.",
              )}
            </span>
          </div>
        )}
      </div>
    </section>
  );
}

export default function AdminOrganisationsPage() {
  const navigate = useNavigate();

  const { getAccessTokenSilently } = useAuth0();

  const { t, i18n } = useTranslation("admin-organisations");

  const [data, setData] = React.useState(null);

  const [loading, setLoading] = React.useState(true);

  const [refreshing, setRefreshing] = React.useState(false);

  const [error, setError] = React.useState("");

  const { showToast } = useToast();

  const [lifecycleAction, setLifecycleAction] = React.useState(null);

  const [organisationTooltip, setOrganisationTooltip] = React.useState({
    visible: false,

    organisationId: null,

    x: 0,

    y: 0,
  });

  const locale =
    i18n.language === "nl"
      ? "nl-BE"
      : i18n.language === "es"
        ? "es-ES"
        : i18n.language === "ca"
          ? "ca-ES"
          : "en-GB";

  function hideOrganisationTooltip() {
    setOrganisationTooltip({
      visible: false,

      organisationId: null,

      x: 0,

      y: 0,
    });
  }

  function moveOrganisationTooltip({ organisationId, x, y }) {
    setOrganisationTooltip({
      visible: true,

      organisationId,

      x,

      y,
    });
  }

  function openOrganisation(organisationId) {
    hideOrganisationTooltip();

    navigate(`/admin/organisations/${organisationId}`);
  }

  const loadOrganisations = React.useCallback(
    async ({ background = false } = {}) => {
      try {
        if (background) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }

        setError("");

        const token = await getAccessTokenSilently();

        const response = await fetch(
          `${API_BASE_URL}/api/admin/organisations/`,
          {
            headers: {
              Authorization: `Bearer ${token}`,

              Accept: "application/json",
            },
          },
        );

        const payload = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            payload.detail ||
              t("errors.load", "Could not load organisation information."),
          );
        }

        setData(payload);
      } catch (err) {
        console.error("Failed to load admin organisations:", err);

        setError(
          err.message ||
            t("errors.load", "Could not load organisation information."),
        );
      } finally {
        if (background) {
          setRefreshing(false);
        } else {
          setLoading(false);
        }
      }
    },
    [getAccessTokenSilently, t],
  );

  React.useEffect(() => {
    loadOrganisations();
  }, [loadOrganisations]);

  function handleRefresh() {
    hideOrganisationTooltip();
    loadOrganisations({ background: true });
  }

  if (loading) {
    return (
      <main className="admin-organisations">
        <div className="admin-organisations-loading">
          <div className="admin-organisations-loading__title" />

          <div className="admin-organisations-loading__panel">
            <div className="admin-organisations-loading__header" />

            <div className="admin-organisations-loading__summary">
              {[1, 2, 3].map((item) => (
                <div
                  key={item}
                  className="admin-organisations-loading__summary-card"
                />
              ))}
            </div>
          </div>

          {[1, 2].map((panel) => (
            <div key={panel} className="admin-organisations-loading__panel">
              <div className="admin-organisations-loading__header" />

              {[1, 2].map((row) => (
                <div key={row} className="admin-organisations-loading__row" />
              ))}
            </div>
          ))}
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="admin-organisations">
        <div className="admin-organisations-error">
          <Building2 size={28} />

          <strong>
            {t("errors.title", "Organisation information unavailable")}
          </strong>

          <p>{error}</p>

          <button type="button" onClick={loadOrganisations}>
            <RefreshCw size={16} />

            {t("actions.retry", "Try again")}
          </button>
        </div>
      </main>
    );
  }

  const summary = data?.summary || {};

  const lenderOrganisations = data?.lender_organisations || data?.lenders || [];

  const ossOrganisations = data?.oss_organisations || data?.oss || [];

  const lenderCount =
    summary.lender_organisations ??
    summary.lenders ??
    lenderOrganisations.length;

  const ossCount =
    summary.oss_organisations ?? summary.oss ?? ossOrganisations.length;

  const investorCount = summary.investor_profiles ?? summary.investors ?? 0;

  return (
    <main className="admin-organisations">
      <header className="admin-organisations__header">
        <div>
          <h1>{t("header.title", "Organisation Context")}</h1>

          <div className="admin-organisations__intro">
            <h2>{t("header.overviewTitle", "Organisations Overview")}</h2>

            <p>
              {t(
                "header.subtitle",

                "Platform organisations connected to users, loan records, renovation projects, and reporting access.",
              )}
            </p>
          </div>
        </div>

        <button
          type="button"
          className="admin-organisations__refresh"
          onClick={handleRefresh}
          disabled={refreshing}
          aria-label={t("actions.refresh", "Refresh organisations")}
          title={t("actions.refresh", "Refresh organisations")}
        >
          <RefreshCw
            size={18}
            strokeWidth={2}
            className={refreshing ? "is-spinning" : ""}
          />

          <span>
            {refreshing
              ? t("actions.refreshing", "Refreshing...")
              : t("actions.refresh", "Refresh")}
          </span>
        </button>
      </header>

      <section className="admin-organisations-panel admin-organisations-panel--summary">
        <header className="admin-organisations-panel__header">
          <div className="admin-organisations-panel__heading">
            <div className="admin-organisations-panel__icon">
              <Building2 size={18} />
            </div>

            <h2>{t("summary.title", "Organisation Summary")}</h2>
          </div>

          <span>
            {summary.focus_label ||
              t("summary.focus", "Belgium / Flanders focus")}
          </span>
        </header>

        <div className="admin-organisations-panel__body">
          <div className="admin-organisations-summary">
            <SummaryItem
              label={t("summary.lenders", "Lender organisations")}
              value={lenderCount}
              locale={locale}
              delay={0}
            />

            <SummaryItem
              label={t("summary.oss", "OSS organisations")}
              value={ossCount}
              locale={locale}
              delay={70}
            />

            <SummaryItem
              label={t("summary.investors", "Investor profiles")}
              value={investorCount}
              locale={locale}
              delay={140}
            />
          </div>
        </div>
      </section>

      <OrganisationSection
        title={t("lenders.title", "Lender Organisations")}
        subtitle={t("lenders.subtitle", "Users, records and amount")}
        organisations={lenderOrganisations}
        type="LENDER"
        locale={locale}
        t={t}
        onOpen={openOrganisation}
        onTooltipMove={moveOrganisationTooltip}
        onTooltipHide={hideOrganisationTooltip}
      />

      <OrganisationSection
        title={t("oss.title", "OSS Organisations")}
        subtitle={t("oss.subtitle", "Users, projects and renovation value")}
        organisations={ossOrganisations}
        type="OSS"
        locale={locale}
        t={t}
        onOpen={openOrganisation}
        onTooltipMove={moveOrganisationTooltip}
        onTooltipHide={hideOrganisationTooltip}
      />

      {organisationTooltip.visible
        ? createPortal(
            <span
              className="admin-organisations-row__floating-hint"
              style={{
                left: organisationTooltip.x + 14,

                top: organisationTooltip.y + 14,
              }}
            >
              {t("actions.openOrganisation", "Open organisation")}
            </span>,

            document.body,
          )
        : null}
    </main>
  );
}
